import { Link } from "react-router-dom";
import { COVER_URL } from "@/lib/api";
import { Play, Heart, MapPin, BookOpen, Share2, NotebookPen } from "lucide-react";
import LatestComments from "@/components/LatestComments";
import NowPlaying from "@/components/NowPlaying";
import SubscribeForm from "@/components/SubscribeForm";
import SocialLinks from "@/components/SocialLinks";
import ContinueListening from "@/components/ContinueListening";

const StickyNote = ({ children, className = "", color = "bg-sticky", rotate = "rotate-[-4deg]" }) => (
  <div className={`relative ${color} text-ink p-3 shadow-xl ${rotate} ${className}`}>
    <div className="tape" />
    <p className="scribble text-base md:text-lg leading-tight">{children}</p>
  </div>
);

export default function Landing() {
  return (
    <div data-testid="landing-page" className="relative">
      {/* Fixed NYC skyline backdrop — stays in place while scrolling */}
      <div className="fixed inset-0 z-0 pointer-events-none" aria-hidden="true">
        <img src="/nyc-skyline.png" alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-ink/70" />
      </div>

      {/* Hero — cinematic NYC skyline backdrop */}
      <section className="relative min-h-[92vh] overflow-hidden grain z-10">
        {/* Hero overlay gradient on top of fixed skyline */}
        <div className="absolute inset-0 bg-gradient-to-b from-ink/40 via-ink/30 to-ink/80 pointer-events-none" />

        <div className="max-w-7xl mx-auto px-5 md:px-10 pt-10 pb-12 sm:pb-20 relative">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">

            {/* LEFT */}
            <div className="relative">
              <div className="mb-3"><NowPlaying variant="pill" /></div>
              <div className="mb-4"><ContinueListening /></div>
              <p className="scribble text-hotpink text-xl sm:text-2xl md:text-3xl mb-3 rotate-[-2deg] inline-block">a journal · a city · a collection of dates we survived</p>

              <h1 className="brush-title leading-[0.82] text-white">
                <span className="block text-6xl sm:text-7xl md:text-8xl lg:text-9xl">IF</span>
                <span className="block text-7xl sm:text-8xl md:text-9xl lg:text-[11rem] text-hotpink -my-2">WE</span>
                <span className="block text-6xl sm:text-7xl md:text-8xl lg:text-9xl">KNEW.</span>
              </h1>
              <p className="scribble text-white/70 text-2xl mt-3 rotate-[-1deg] inline-block">we would make better choices.</p>

              <p className="mt-5 font-outfit text-lg md:text-xl text-white/80 max-w-md">
                A <span className="text-hotpink font-bold uppercase tracking-widest">dating diary</span> audio series by Joey. Real dates. Real thoughts. Real unhinged.
              </p>

              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Link to="/episodes" data-testid="hero-cta-listen" className="bg-hotpink hover:bg-hotpink_dark text-white font-outfit font-extrabold uppercase tracking-widest text-sm px-7 py-4 shadow-[6px_6px_0px_#FFFFFF] hover:shadow-[8px_8px_0px_#FFFFFF] transition-all flex items-center gap-3">
                  <Play size={18} fill="white" /> Listen — first 10 free
                </Link>
                <div className="flex items-center gap-3">
                  <Link to="/about" data-testid="hero-cta-about" className="text-white font-outfit border-2 border-white/30 px-5 py-3 hover:border-hotpink hover:text-hotpink transition-colors uppercase tracking-widest text-sm">
                    Meet Joey
                  </Link>
                  <Link to="/episodes#topic-of-the-week" data-testid="hero-cta-topic" className="text-hotpink font-outfit font-bold bg-white border-2 border-white px-3 py-2 hover:bg-hotpink hover:text-white transition-colors uppercase tracking-widest text-xs flex items-center gap-2 shadow-[4px_4px_0px_#FF007F] hover:shadow-[6px_6px_0px_#FFFFFF]">
                    <NotebookPen size={13} /> Weekly Topic
                  </Link>
                </div>
              </div>

              <p className="scribble text-white/60 text-xl mt-5 rotate-[-1deg] inline-block" data-testid="pricing-line">
                10 episodes free → then $1 each. <span className="text-hotpink">no subscription. just receipts.</span>
              </p>

              <p className="font-outfit text-white/50 text-xs uppercase tracking-widest mt-4 max-w-md" data-testid="real-events-disclosure">
                Based on real-life events. Names &amp; identifying details have been changed.
              </p>
            </div>

            {/* RIGHT: cover + scattered sticky notes */}
            <div className="relative h-[360px] sm:h-[480px] md:h-[640px]">
              <div className="absolute top-0 right-0 z-20 hidden sm:block" data-testid="hero-socials">
                <SocialLinks size="sm" />
              </div>
              <div className="absolute inset-0 flex items-center justify-center">
                <img src={COVER_URL} alt="If We Knew cover" className="w-[88%] sm:w-[82%] max-w-md shadow-2xl rotate-[2deg] border-4 border-white/20" data-testid="hero-cover-art" />
              </div>

              <StickyNote className="absolute top-2 right-2 w-32 sm:w-44 animate-float-slow z-10 hidden sm:block" color="bg-sticky_pink" rotate="rotate-[6deg]">
                Great chemistry. Trash personality.
              </StickyNote>

              <StickyNote className="absolute top-32 -left-2 w-32 sm:w-40 animate-float-slow-2 z-10 hidden sm:block" color="bg-sticky" rotate="rotate-[-5deg]">
                Do people really do this? (apparently yes.)
              </StickyNote>

              <StickyNote className="absolute bottom-20 right-4 w-40 sm:w-44 z-10 hidden sm:block" color="bg-paper" rotate="rotate-[4deg]">
                WOULD NOT RECOMMEND.
              </StickyNote>

              <StickyNote className="absolute bottom-2 -left-4 w-44 sm:w-48 z-10 hidden sm:block" color="bg-sticky_pink" rotate="rotate-[-3deg]">
                If we knew... we wouldn't have learned the hard way. ♡
              </StickyNote>
            </div>
            {/* Mobile-only socials, below the cover */}
            <div className="flex sm:hidden justify-center -mt-4 mb-2">
              <SocialLinks size="sm" />
            </div>
          </div>
        </div>
      </section>

      {/* Three pillars */}
      <section className="bg-ink/85 backdrop-blur-sm py-20 md:py-28 border-t border-white/5 relative z-10">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <p className="scribble text-hotpink text-2xl rotate-[-2deg] inline-block mb-4">the deal</p>
          <h2 className="brush-title text-5xl md:text-7xl text-white max-w-3xl mb-12">
            Ten dates. <span className="text-hotpink">free.</span> The rest? <span className="pink-underline">a dollar each.</span>
          </h2>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-coal border border-white/10 p-8 hover:border-hotpink/60 transition-colors">
              <BookOpen className="text-hotpink mb-4" size={28} />
              <h3 className="font-outfit font-bold text-2xl text-white mb-2">10 episodes free</h3>
              <p className="text-white/60 font-outfit">Start with episode one. Joey's first ten dates after a 12-year relationship. Just press play.</p>
            </div>
            <div className="bg-coal border border-white/10 p-8 hover:border-hotpink/60 transition-colors">
              <Heart className="text-hotpink mb-4" size={28} />
              <h3 className="font-outfit font-bold text-2xl text-white mb-2">$1 per episode</h3>
              <p className="text-white/60 font-outfit">After ten, each new episode is one dollar. Card or Apple Pay. Yours forever.</p>
            </div>
            <div className="bg-coal border border-white/10 p-8 hover:border-hotpink/60 transition-colors">
              <MapPin className="text-hotpink mb-4" size={28} />
              <h3 className="font-outfit font-bold text-2xl text-white mb-2">Written in NYC</h3>
              <p className="text-white/60 font-outfit">Mis-Match dates, Brooklyn brunches, midtown mishaps. Joey narrates — no filters.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Share teaser */}
      <section className="bg-coal/80 backdrop-blur-sm py-20 md:py-28 border-t border-white/5 relative z-10">
        <div className="max-w-3xl mx-auto px-5 md:px-10">
          <div className="bg-ink border border-hotpink/40 p-8 md:p-10 relative overflow-hidden">
            <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-hotpink/10 blur-3xl rounded-full" />
            <Share2 className="text-hotpink mb-4" size={32} />
            <p className="scribble text-hotpink text-2xl mb-1 rotate-[-2deg] inline-block">spread the chaos</p>
            <h3 className="brush-title text-4xl md:text-5xl text-white mb-4">SHARE = <span className="text-hotpink">1 FREE</span> EPISODE.</h3>
            <p className="text-white/70 font-outfit mb-5">
              Tap share once. We'll give you one paid episode on the house. Pick any locked one — even episode 15.
            </p>
            <p className="scribble text-white/80 text-xl rotate-[-1deg] inline-block mb-6">tell a friend. unlock a date.</p>
            <Link to="/episodes" className="inline-block bg-white text-ink hover:bg-hotpink hover:text-white font-outfit font-extrabold uppercase tracking-widest text-xs px-6 py-3 shadow-[4px_4px_0px_#FF007F] hover:shadow-[6px_6px_0px_#FFFFFF] transition-all">
              go to episodes →
            </Link>
          </div>
        </div>
      </section>

      {/* Latest community comments */}
      <section className="bg-ink/85 backdrop-blur-sm py-16 md:py-20 border-t border-white/5 relative z-10">
        <div className="max-w-6xl mx-auto px-5 md:px-10">
          <div className="mb-10 grid md:grid-cols-2 gap-4 items-stretch">
            <SubscribeForm />
            <div className="bg-coal border border-hotpink/40 p-5 md:p-6 flex flex-col justify-center">
              <p className="scribble text-hotpink text-2xl mb-1 rotate-[-2deg] inline-block">follow the chaos</p>
              <h3 className="font-outfit font-bold text-white text-lg mb-3">Behind-the-scenes on socials</h3>
              <p className="text-white/60 font-outfit text-sm mb-4">Daily reels, voice memos, polls about your worst dates.</p>
              <SocialLinks size="md" />
            </div>
          </div>
          <LatestComments limit={8} title="THE THREAD." subtitle="what everyone's saying" />
        </div>
      </section>

      {/* About teaser */}
      <section className="bg-ink/85 backdrop-blur-sm py-20 md:py-28 relative z-10">
        <div className="max-w-6xl mx-auto px-5 md:px-10 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <p className="scribble text-hotpink text-2xl mb-2 rotate-[-2deg] inline-block">written & narrated by</p>
            <h2 className="brush-title text-6xl md:text-8xl text-white mb-6">JOEY.</h2>
            <p className="font-outfit text-white/80 text-lg leading-relaxed mb-4">
              Joana "Joey" Alvarez is 36, sharp, and knows exactly how to spot a red flag—because she gets paid to.
            </p>
            <p className="font-outfit text-white/70 leading-relaxed mb-6">
              By day, she's a merchandiser for a luxury watch company in New York. By night, she runs <span className="text-hotpink font-bold">Red Flags</span>, a discreet side business where clients hire her to investigate the people they're dating.
            </p>
            <p className="scribble text-2xl text-hotpink rotate-[-1deg] inline-block">so why is her own love life a mess?</p>
            <p className="brush-title text-2xl text-white mt-5">If we knew… would we make better choices?</p>
            <div className="mt-6">
              <Link to="/about" data-testid="about-cta" className="text-white font-outfit border-b-2 border-hotpink pb-1 hover:text-hotpink transition-colors uppercase tracking-widest text-sm">
                read the whole story →
              </Link>
            </div>
          </div>
          <div className="relative">
            <img src={COVER_URL} alt="cover" className="w-full max-w-sm mx-auto rotate-[-2deg] shadow-2xl border-4 border-white/15" />
            <StickyNote className="absolute -top-4 -right-2 w-40 z-10" color="bg-paper" rotate="rotate-[5deg]">
              RED FLAGS i ignored.
            </StickyNote>
          </div>
        </div>
      </section>

      <footer className="bg-ink/90 backdrop-blur-sm border-t border-white/10 py-10 relative z-10">
        <div className="max-w-7xl mx-auto px-5 md:px-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <p className="brush-title text-2xl text-white">IF <span className="text-hotpink">WE</span> KNEW.</p>
          <SocialLinks size="md" />
          <div className="text-center md:text-right">
            <a
              href="mailto:ifweknewaudio@outlook.com"
              data-testid="contact-email-footer"
              className="block text-white/70 hover:text-hotpink font-outfit text-sm transition-colors"
            >
              ifweknewaudio@outlook.com
            </a>
            <p className="text-white/40 font-outfit text-sm mt-1">© Joey · NYC · made with too much wine</p>
          </div>
        </div>
        <p className="max-w-3xl mx-auto px-5 md:px-10 text-center md:text-right text-white/30 font-outfit text-xs mt-6 leading-relaxed">
          Stories on this site are based on real-life events. Names and identifying details have been changed to protect the (not-so) innocent.
        </p>
      </footer>
    </div>
  );
}
